import {
  Badge,
  Card,
  CopyableEntry,
  Heading,
  TwoColumn,
} from '@dittolive/anvil'
import { ApiKeyManager } from '@portal/core/scenes/connect'
import type { PortalApiKey } from '@portal/core/types'
import graphql from 'babel-plugin-relay/macro'
import ReadTheDocsLink from 'components/docsLink/ReadTheDocsLink'
import { useAppContext } from 'context'
import React, { Suspense, useCallback, useEffect } from 'react'
import {
  PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { useNavigate } from 'react-router-dom'
import { usePortalMutation } from 'utils'
import { useFeatureFlags } from 'utils/featureFlags'

import { AppAuthAPIConnectionSectionApiKeysQuery } from './__generated__/AppAuthAPIConnectionSectionApiKeysQuery.graphql'
import { AppAuthAPIConnectionSectionDeleteApiKeyMutation } from './__generated__/AppAuthAPIConnectionSectionDeleteApiKeyMutation.graphql'
import { AppAuthAPIConnectionSectionQuery } from './__generated__/AppAuthAPIConnectionSectionQuery.graphql'

export const query = graphql`
  query AppAuthAPIConnectionSectionQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        cloudUrls {
          url
          wsEnabled
          httpEnabled
        }
        viewerCanReadApiKey
        viewerCanCreateApiKey
        viewerCanDeleteApiKey
      }
    }
  }
`

const apiKeysQuery = graphql`
  query AppAuthAPIConnectionSectionApiKeysQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        # eslint-disable-next-line relay/unused-fields
        apiKeys {
          id
          description
          createdAt
          lastUsedAt
          expiresAt
          viewerCanDelete
          createdBy {
            ... on User {
              firstName
              lastName
            }
            ... on RestrictedApiKeyCreator {
              firstName
              lastName
            }
          }
          permission {
            remoteQuery
            read {
              everything
              queriesByCollection
            }
            write {
              everything
              queriesByCollection
            }
          }
        }
      }
    }
  }
`

const deleteApiKeyMutation = graphql`
  mutation AppAuthAPIConnectionSectionDeleteApiKeyMutation($apiKeyId: ID!) {
    deleteApiKey(id: $apiKeyId) {
      app {
        apiKeys {
          id
        }
      }
    }
  }
`

type ContainerProps = {
  appId: string
}

export default function AppAuthAPIConnectionSectionContainer({
  appId,
}: ContainerProps) {
  const [queryRef, loadQuery] =
    useQueryLoader<AppAuthAPIConnectionSectionQuery>(query)

  useEffect(() => {
    if (appId) {
      loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
    }
  }, [appId, loadQuery])

  if (!queryRef || !appId) {
    return null
  }

  return (
    <Suspense>
      <AppAuthAPIConnectionSection appId={appId} queryRef={queryRef} />
    </Suspense>
  )
}

type CloudUrlCardProps = Pick<
  NonNullable<
    AppAuthAPIConnectionSectionQuery['response']['viewer']['appById']
  >,
  'cloudUrls'
>

function CloudUrlCard({ cloudUrls }: CloudUrlCardProps) {
  const wsEnabled = cloudUrls[0]?.wsEnabled
  const httpEnabled = cloudUrls[0]?.httpEnabled

  return (
    <Card isDivided={false}>
      <Card.Header>
        <div className="pb-4">
          <Heading level={3} className="block">
            Big peer connection information
          </Heading>
          <p className="text-foreground-subtle font-sans text-base font-normal">
            Connect to Ditto with your cloud URL
          </p>
        </div>
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        <CopyableEntry
          title="Cloud URL Endpoint"
          testId="cloudUrl"
          value={cloudUrls[0]?.url}
          render={() =>
            cloudUrls.map((cloudUrl) => (
              <div key={cloudUrl.url}>{cloudUrl.url}</div>
            ))
          }
          isCode
        />

        <div className="flex flex-wrap items-center gap-3">
          <Badge colorScheme={httpEnabled ? 'green' : 'red'}>
            HTTP {httpEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Badge colorScheme={wsEnabled ? 'green' : 'red'}>
            Websocket {wsEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  )
}

type Props = {
  appId: string
  queryRef: PreloadedQuery<AppAuthAPIConnectionSectionQuery>
}

type ApiKeysCardProps = {
  appId: string
  canCreateApiKeys: boolean
  canDeleteApiKeys: boolean
}

function ApiKeysCard({
  appId,
  canCreateApiKeys,
  canDeleteApiKeys,
}: ApiKeysCardProps) {
  const { isSuperAdmin } = useAppContext()
  const navigate = useNavigate()

  const { viewer } = useLazyLoadQuery<AppAuthAPIConnectionSectionApiKeysQuery>(
    apiKeysQuery,
    { appId },
  )

  const apiKeys = (viewer.appById?.apiKeys || []) as PortalApiKey[]

  const [deleteApiKey] =
    usePortalMutation<AppAuthAPIConnectionSectionDeleteApiKeyMutation>(
      deleteApiKeyMutation,
      {
        mutationName: 'DeleteApiKey',
        sentryContext: { component: 'AppAuthAPIConnectionSection' },
      },
    )

  const handleDeleteApiKey = useCallback(
    async (apiKeyId: string) => {
      return new Promise<void>((resolve, reject) => {
        deleteApiKey({
          variables: { apiKeyId },
          onCompleted: () => resolve(),
          onError: (err) => reject(err),
        })
      })
    },
    [deleteApiKey],
  )

  const handleCreateClick = useCallback(() => {
    navigate('api-keys/new')
  }, [navigate])

  const canFullyManageApiKeys = canCreateApiKeys && canDeleteApiKeys
  let actionText: string | undefined
  if (canFullyManageApiKeys) {
    actionText = 'Create and manage'
  } else if (canCreateApiKeys) {
    actionText = 'Create and view'
  } else {
    actionText = 'View'
  }

  return (
    <Card isDivided={false}>
      <Card.Header>
        <div>
          <Heading level={3} className="block">
            API keys
          </Heading>
          <p className="text-foreground-subtle font-sans text-base font-normal">
            {actionText} API keys to authenticate via HTTP.{' '}
            <ReadTheDocsLink docsArea="apiKey" />
          </p>
        </div>
      </Card.Header>
      <Card.Spacer />
      <Card.Body isFlushed>
        <ApiKeyManager
          apiKeys={apiKeys as PortalApiKey[]}
          canCreate={canCreateApiKeys}
          canDelete={canDeleteApiKeys}
          isSuperAdmin={isSuperAdmin}
          onCreateClick={handleCreateClick}
          onDelete={handleDeleteApiKey}
        />
      </Card.Body>
    </Card>
  )
}

function AppAuthAPIConnectionSection({
  appId,
  queryRef,
}: Props): React.ReactElement {
  const { viewer } = usePreloadedQuery(query, queryRef)
  const { unifiedConnectionUrl } = useFeatureFlags()

  const cloudUrls = viewer.appById?.cloudUrls || []

  const canReadApiKeys = !!viewer.appById?.viewerCanReadApiKey
  const canCreateApiKeys = !!viewer.appById?.viewerCanCreateApiKey
  const canDeleteApiKeys = !!viewer.appById?.viewerCanDeleteApiKey

  const renderColumnContent = () => {
    return (
      <p>
        Everything you need to get connected to Ditto through our HTTP API.{' '}
        <ReadTheDocsLink docsArea="httpApi" />
      </p>
    )
  }

  return (
    <TwoColumn
      firstColumnTitle="Connect via HTTP"
      firstColumnContent={renderColumnContent()}
    >
      <div className="flex flex-col gap-8">
        {!unifiedConnectionUrl && <CloudUrlCard cloudUrls={cloudUrls} />}

        {canReadApiKeys && (
          <Suspense fallback={null}>
            <ApiKeysCard
              appId={appId}
              canCreateApiKeys={canCreateApiKeys}
              canDeleteApiKeys={canDeleteApiKeys}
            />
          </Suspense>
        )}
      </div>
    </TwoColumn>
  )
}
